import React from "react";
import dayjs from "dayjs";
import {
    ActionCellRenderer,
    CampaignCreationProductCellRenderer,
    ChangeCellRenderer,
    CityCellRenderer,
    CitySelectionDropdownWithCohortCellRenderer,
    PriorityCellRenderer,
    VariantsCellRenderer,
} from "@/components/widgets/WidgetCellRenderers";
import { BCC_BID_SUGGESTION_TYPES, BCC_KEYWORD_TYPES, CommonWidgetMetrics, KWD_BID_TYPES, RevstackFilterTypes, IBCCKeyword, IKeywordCohort, ICategoryTarget, ICollection, CLUSTER_ACCORDION_TYPES, BCC_CAMPAIGN_TYPES, KeywordTypeValues } from "../common-types";
import { PINNED_COL_MAP } from "../revstack/constants";
import { Button, Input, Select, Space, Tag, Typography, Popconfirm } from "antd";
import { DeleteOutlined, CopyOutlined, CloseOutlined } from "@ant-design/icons";
import { disableCampaignsAddition, titleCase, internationalizeNumericValue, parseInternationalizedValue, validateMaxCampaignBudget, startCase } from "../utils";
import { BsPlusLg, BsDashLg } from "react-icons/bs";
import {
    BidCellRenderer,
    ExactMatchBidInput,
    SmartMatchBidCell,
    EditableCampaignNameCell
} from "./ui-utils";
import { BCC_KEYWORD_TYPE_ABBREVIATIONS, CAMPAIGN_CREATION_DEFAULT_OPTIONS } from "../constants";

const { Text } = Typography;

//!Note: This files has to be replanned as the move toward config driven developmewnt for newer modules

const CLUSTER_TABLE_BASE_CONFIG = {
    isGroupedTable: false,
    isSearchReq: true,
    isDrilldownTable: false,
    isCompetitionButtonReq: false,
    isLayoutButtonReq: false,
    isDownloadButtonReq: false,
    isExpandable: false,
    isCheckboxSelectionRequired: true,
    isSelectAllRequired: true,
    style: {
        height: 400,
        expandedHeight: "51vh",
    },
    additionalFilters: [
        {
            section: "",
            key: CommonWidgetMetrics.CATEGORY,
            type: RevstackFilterTypes.SELECT,
            props: {
                options: [CAMPAIGN_CREATION_DEFAULT_OPTIONS.category],
                onChange: (value: string) => {},
                defaultValue: CommonWidgetMetrics.All,
                width: 138,
            },
        },
        {
            section: "",
            key: CommonWidgetMetrics.SUB_CATEGORY,
            type: RevstackFilterTypes.SELECT,
            props: {
                options: [CAMPAIGN_CREATION_DEFAULT_OPTIONS.subcategory],
                onChange: (value: string) => {},
                defaultValue: CommonWidgetMetrics.All,
                width: 162,
            },
        },
    ],
};

const COMMON_CLUSTER_COLUMNS = [
    { ...PINNED_COL_MAP[CommonWidgetMetrics.SKU],
        title: "PRODUCTS",
        render: (text: string, record: any) =>
            CampaignCreationProductCellRenderer(titleCase(text), record, {
                isImageRequired: true,
                isLinkRequired: true,
            }),
    },
    {
        title: "PRIORITY",
        dataIndex: CommonWidgetMetrics.PRIORITY,
        key: CommonWidgetMetrics.PRIORITY,
        minWidth: 100,
        maxWidth: 100,
        width: 100,
        align: "center",
        render: (value: any, record: any) =>
            PriorityCellRenderer(record[CommonWidgetMetrics.PRIORITY], record),
    },
    {
        title: "VARIANTS",
        dataIndex: `${CommonWidgetMetrics.VARIANTS}_value`,
        key: CommonWidgetMetrics.VARIANTS,
        minWidth: 232,
        maxWidth: 232,
        width: 232,
        align: "center",
        render: (value: any, record: any) =>
            <VariantsCellRenderer
                value={record[`${CommonWidgetMetrics.VARIANTS}_value`]}
                record={record}
            />,
    },
];

export const getClusterCreationTableConfig = () => {
    const additionalColumns = [
        {
            title: "MARKET SHARE",
            dataIndex: `${CommonWidgetMetrics.MARKET_SHARE}_pct`,
            key: "market_share_pct",
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "center",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.MARKET_SHARE,
                    value,
                    record[`${CommonWidgetMetrics.MARKET_SHARE}_pct_change`],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(a[`${CommonWidgetMetrics.MARKET_SHARE}_pct`]) ||
                    0;
                const bVal =
                    parseFloat(b[`${CommonWidgetMetrics.MARKET_SHARE}_pct`]) ||
                    0;
                return aVal - bVal;
            },
        },
        {
            title: "ORG. SOV",
            dataIndex: `${CommonWidgetMetrics.ORGANIC_SOV}`,
            key: CommonWidgetMetrics.ORGANIC_SOV,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "center",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.ORGANIC_SOV,
                    value,
                    record[`${CommonWidgetMetrics.ORGANIC_SOV}_change`],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(a[`${CommonWidgetMetrics.ORGANIC_SOV}`]) || 0;
                const bVal =
                    parseFloat(b[`${CommonWidgetMetrics.ORGANIC_SOV}`]) || 0;
                return aVal - bVal;
            },
        },
        {
            title: "PAID SOV",
            dataIndex: `${CommonWidgetMetrics.PAID_SOV}`,
            key: CommonWidgetMetrics.PAID_SOV,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "center",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.PAID_SOV,
                    value,
                    record[`${CommonWidgetMetrics.PAID_SOV}_change`],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(a[`${CommonWidgetMetrics.PAID_SOV}`]) || 0;
                const bVal =
                    parseFloat(b[`${CommonWidgetMetrics.PAID_SOV}`]) || 0;
                return aVal - bVal;
            },
        },
        {
            title: "WT. AVAIL",
            dataIndex: `${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_value`,
            key: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            align: "center",
            render: (value: any, record: any) =>
                ChangeCellRenderer(
                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                    value,
                    record[
                        `${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_change`
                    ],
                    "bottom"
                ),
            sorter: (a: any, b: any) => {
                const aVal =
                    parseFloat(
                        a[`${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_value`]
                    ) || 0;
                const bVal =
                    parseFloat(
                        b[`${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_value`]
                    ) || 0;
                return aVal - bVal;
            },
        },
    ];

    return {
        ...CLUSTER_TABLE_BASE_CONFIG,
        isSelectAllRequired: false,
        colDefs: [...COMMON_CLUSTER_COLUMNS, ...additionalColumns],
    };
};

export const getClusterAccordionTableConfig = (showActionColumn = true) => {
    const additionalColumns = showActionColumn ? [
        {
            title: "ACTION",
            dataIndex: `${CommonWidgetMetrics.ACTION}`,
            key: CommonWidgetMetrics.ACTION,
            minWidth: 70,
            maxWidth: 70,
            width: 70,
            align: "center",
            render: (value: any, record: any) =>
                ActionCellRenderer(
                    record[`${CommonWidgetMetrics.ACTION}`],
                    record,
                    record.clusterId
                ),
        },
    ] : [];

    return {
        ...CLUSTER_TABLE_BASE_CONFIG,
        isCheckboxSelectionRequired: false,
        isSearchReq: false,
        colDefs: [...COMMON_CLUSTER_COLUMNS, ...additionalColumns],
        additionalFilters: [] as any,
    };
};

export const getCampaignCreationTableConfig = (params: any) => {
    const {
        clientCurrencySymbol,
        handleDuplicate,
        handleDelete,
        handleFieldChange,
        handleAddKeywords,
        handleEditCohort,
        setSelectedKeywords,
        isRowExpanded,
        handleCampaignCollectionBreakdown,
        citiesList,
        expandedRowKeys,
        setExpandedRowKeys,
        expandedRowRender,
        rowExpandable,
        handleAddAssets
    } = params;
    return {
        colDefs: [
            {
                title: "ACTIONS",
                key: "actions",
                width: 100,
                fixed: "left",
                showRowCount: false,
                render: (_: any, record: any) => {
                    return (
                        <Space>
                            <Button
                                icon={<CopyOutlined />}
                                style={{ padding: "4px 8px", height: "auto" }}
                                onClick={() => handleDuplicate(record)}
                                disabled={disableCampaignsAddition(record)}
                            />
                            <Button
                                icon={<DeleteOutlined />}
                                danger
                                style={{ padding: "4px 8px", height: "auto" }}
                                onClick={() => handleDelete(record.id)}
                            />
                        </Space>
                    );
                },
            },
            {
                title: "CAMPAIGN TYPE",
                dataIndex: "campaignType",
                key: "campaignType",
                width: 193,
                align: "left",
                render: (value: string, record: any) => (
                    <Select
                        value={value || undefined}
                        placeholder="Select"
                        style={{ width: "100%" }}
                        options={[
                            {
                                label: BCC_CAMPAIGN_TYPES.PRODUCT_BOOSTER,
                                value: BCC_CAMPAIGN_TYPES.PRODUCT_BOOSTER,
                            },
                            // {
                            //     label: BCC_CAMPAIGN_TYPES.RECOMMENDATION_ADS,
                            //     value: BCC_CAMPAIGN_TYPES.RECOMMENDATION_ADS,
                            // }
                        ]}
                        onChange={(newValue) =>
                            handleFieldChange(
                                record.id,
                                "campaignType",
                                newValue,
                                record
                            )
                        }
                    />
                ),
            },
            {
                title: "KEYWORD GROUPS",
                dataIndex: "targets",
                key: "targets",
                width: 450,
                align: "left",
                render: (value: any, record: any) => {
                    const cohorts = record.keywordCohorts || [];
                    const canAddMore = cohorts.length < 5;

                    if(record?.campaignType === BCC_CAMPAIGN_TYPES.RECOMMENDATION_ADS) {
                        return (
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'flex-end',
                                width: '100%',
                                gap: '8px'
                            }}>
                                <Button
                                    type="primary"
                                    style={{ 
                                        fontWeight: 600,
                                        width: '150px',
                                        flexShrink: 0
                                    }}
                                    onClick={() => handleAddAssets(record.clusterId, record.id)}
                                >
                                    Add Assets
                                </Button>
                            </div>
                        );
                    }
                    return (
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            width: '100%',
                            gap: '8px'
                        }}>
                            {/* Cohorts container with scroll */}
                            <div className="border rounded px-1" style={{ 
                                flex: 1,
                                overflowX: 'auto',
                                overflowY: 'hidden',
                                whiteSpace: 'nowrap',
                                display: 'flex',
                                gap: '4px',
                                alignItems: 'center',
                                minWidth: 0,
                                height: '32px'
                            }}>
                                {cohorts.length === 0 ? (
                                    <Text className="city-selection-dropdown__placeholder">
                                        Select on Add Targets
                                    </Text>
                                ) : cohorts.map((cohort: IKeywordCohort, index: number) => {
                                    const hasKeywordTypes = cohort.keywordTypes?.length > 0;
                                    const hasCategories = cohort.categories?.length > 0;
                                    const hasCustomKeywords = cohort.keywords?.some((kw: IBCCKeyword) => kw.isCustom || kw.keyword_type === KeywordTypeValues.CUSTOM);
                                    
                                    let tagLabel = '';
                                    if (hasKeywordTypes) {
                                        tagLabel = cohort.keywordTypes.map((type: string) => BCC_KEYWORD_TYPE_ABBREVIATIONS[type] || titleCase(type)).join('+ ');
                                    } else if (hasCustomKeywords) {
                                        tagLabel = 'Custom';
                                    } else if (hasCategories) {
                                        tagLabel = 'Category';
                                    }
                                    
                                    return (
                                        <Tag
                                            key={cohort.id || index}
                                            closeIcon={
                                                <Popconfirm
                                                    title="Are you sure you want to remove this Keyword Group?"
                                                    onConfirm={() => {
                                                        handleFieldChange(
                                                            record.id,
                                                            "deleteCohort",
                                                            cohort.id,
                                                            record
                                                        );
                                                    }}
                                                    okText="Remove"
                                                    cancelText="Cancel"
                                                    placement="top"
                                                    icon={null}
                                                >
                                                    <CloseOutlined 
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{ fontSize: '10px', marginLeft: '4px' }}
                                                    />
                                                </Popconfirm>
                                            }
                                            onClose={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                            style={{ 
                                                margin: 0,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <span 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditCohort?.(record, cohort.id);
                                                }}
                                                className="cohort-tag-label"
                                                style={{ 
                                                    cursor: 'pointer',
                                                    color: '#1890ff',
                                                    textDecoration: 'none',
                                                }}
                                                onMouseEnter={(e) => {
                                                    (e.target as HTMLSpanElement).style.textDecoration = 'underline';
                                                }}
                                                onMouseLeave={(e) => {
                                                    (e.target as HTMLSpanElement).style.textDecoration = 'none';
                                                }}
                                            >
                                                {tagLabel || 'Cohort'}
                                            </span>
                                        </Tag>
                                    );
                                })}
                            </div>
                            {/* Add targets button */}
                            <Button
                                type="default"
                                style={{ 
                                    fontWeight: 600,
                                    width: '100px',
                                    flexShrink: 0
                                }}
                                onClick={() => handleAddKeywords(record)}
                                disabled={!canAddMore}
                                title={!canAddMore ? "Maximum 5 cohorts allowed" : ""}
                            >
                                Add Targets
                            </Button>
                        </div>
                    );
                },
            },
            {
                title: "CITY",
                dataIndex: "city",
                key: "city",
                width: 255,
                align: "left",
                render: (value: string[], record: ICollection) => (
                    <CitySelectionDropdownWithCohortCellRenderer
                        value={value}
                        record={record}
                        citiesList={citiesList}
                        handleFieldChange={handleFieldChange}
                    />
                ),
            },
            {
                title: "TIME SLOT",
                dataIndex: "timeSlot",
                key: "timeSlot",
                width: 129,
                align: "left",
                render: (value: string, record: any) => (
                    <Select
                        mode="multiple"
                        value={value || undefined}
                        placeholder="All Day"
                        style={{ width: "100%" }}
                        disabled={true}
                        options={[
                            { value: "MORNING", label: "Morning" },
                            { value: "AFTERNOON", label: "Afternoon" },
                            { value: "EVENING", label: "Evening" },
                            { value: "NIGHT", label: "Night" },
                        ]}
                        onChange={(newValue) =>
                            handleFieldChange(
                                record.id,
                                "timeSlot",
                                newValue,
                                record
                            )
                        }
                    />
                ),
            },
            {
                title: "BUDGET",
                dataIndex: "budget",
                key: "budget",
                width: 108,
                align: "left",
                render: (value: string, record: any) => (
                    <Input
                        type="text"
                        value={value ? internationalizeNumericValue(value) : ''}
                        placeholder=""
                        style={{ width: "100%" }}
                        prefix={clientCurrencySymbol}
                        onChange={(e) => {
                            const numericValue = parseInternationalizedValue(e.target.value);
                            if (!validateMaxCampaignBudget(numericValue)) {
                                return;
                            }
                            handleFieldChange(
                                record.id,
                                "budget",
                                isNaN(numericValue) ? 0 : numericValue,
                                record
                            );
                        }}
                    />
                ),
            },
            {
                title: "TYPE",
                dataIndex: "type",
                key: "type",
                width: 100,
                align: "left",
                render: (value: string, record: any) => (
                    <Select
                        value={value || undefined}
                        placeholder="Select"
                        style={{ width: "100%" }}
                        options={[
                            { label: "Multiple", value: "multiple" },
                            { label: "Single", value: "single" },
                        ]}
                        onChange={(newValue) =>
                            handleFieldChange(
                                record.id,
                                "type",
                                newValue,
                                record
                            )
                        }
                    />
                ),
            },
            {
                title: "CAMPAIGNS",
                dataIndex: "campaigns",
                key: "campaigns",
                width: 20,
                align: "left",
                hidden: true,
            },
            {
                title: "CAMPAIGNS COUNT",
                dataIndex: "campaignsCount",
                key: "campaignsCount",
                width: 157,
                align: "left",
                fixed: "right",
                render: (value: string, record: any) => {
                    const nonArchivedCampaigns =
                        record.campaigns?.filter(
                            (campaign: any) => !campaign.isArchived
                        ) || [];
                    const campaignCount = nonArchivedCampaigns.length;

                    return (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                            }}
                            onClick={() => {
                                if (
                                    record?.type === "multiple" &&
                                    campaignCount > 0
                                ) {
                                    handleCampaignCollectionBreakdown(record);
                                }
                            }}
                        >
                            <Tag
                                style={{
                                    backgroundColor: "#E5F8FF",
                                    borderColor: "#B3E9FF",
                                    color: "#0078AA",
                                    width: "102px",
                                    height: "28px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "4px 8px",
                                }}
                            >
                                {campaignCount} {campaignCount <= 1 ? 'campaign' : 'campaigns'}
                            </Tag>
                            {record?.type === "multiple" &&
                                campaignCount > 0 && (
                                    <Button
                                        type="text"
                                        shape="circle"
                                        size="small"
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: "23px",
                                            height: "24px",
                                            padding: "8px",
                                            gap: "8px",
                                            border: "1px solid #CBD9E7",
                                        }}
                                        icon={
                                            expandedRowKeys?.includes(
                                                `${record.clusterId}-${record.id}`
                                            ) ? (
                                                <BsDashLg size={16} />
                                            ) : (
                                                <BsPlusLg size={16} />
                                            )
                                        }
                                    />
                                )}
                        </div>
                    );
                },
            },
        ],
        isGroupedTable: false,
        isSearchReq: false,
        isDrilldownTable: false,
        isCompetitionButtonReq: false,
        isLayoutButtonReq: false,
        isDownloadButtonReq: false,
        isExpandable: true,
        isCheckboxSelectionRequired: false,
        style: {
            height: 600,
        },
        expandedRowKeys,
        setExpandedRowKeys,
        expandedRowRender,
        rowExpandable,
    };
};

export const getCampaignBreakdownTableConfig = (params: any) => {
    const {
        clientCurrencySymbol,
        handleDelete,
        handleFieldChange,
        handleCampaignNameChange,
        expandedRowKeys,
        setExpandedRowKeys,
        expandedRowRender,
        rowExpandable,
    } = params;
    return {
        colDefs: [
            {
                title: "COLLECTION",
                key: "collection",
                width: 300,
                render: (_: any, record: any) => {
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Typography.Text strong style={{ fontWeight: 700, fontSize: '14px', color: '#0078AA' }}>
                                    {record.clusterName}
                                </Typography.Text>
                                {record.clusterType === CLUSTER_ACCORDION_TYPES.CLUSTER && <Tag 
                                    className="cluster-tag" 
                                >
                                    {record.clusterType}
                                </Tag>}
                            </div>
                            <Typography.Text style={{ fontSize: '12px', color: '#6B7280' }}>
                                Products: {record.productsCount}
                            </Typography.Text>
                        </div>
                    );
                },
            },
            {
                title: "BUDGET",
                dataIndex: "budget",
                key: "budget",
                width: 150,
                align: "left",
                render: (value: string) => value ? `${clientCurrencySymbol || ''} ${internationalizeNumericValue(value)}` : '',
            },
            {
                title: "START DATE",
                dataIndex: "startDate",
                key: "startDate",
                width: 150,
                align: "left",
                render: (value: string) => {
                    if (!value || value === '-') return '-';
                    return dayjs(value, 'DD/MM/YYYY').format('DD MMM YYYY');
                },
            },
            {
                title: "END DATE",
                dataIndex: "endDate",
                key: "endDate",
                width: 150,
                align: "left",
                render: (value: string) => {
                    if (!value || value === '-') return '-';
                    return dayjs(value, 'DD/MM/YYYY').format('DD MMM YYYY');
                },
            },
            {
                title: "TOTAL CAMPAIGNS",
                dataIndex: "totalCampaigns",
                key: "totalCampaigns",
                width: 150,
                align: "center",
                render: (value: number) =>
                    value !== undefined && value !== null
                        ? `${
                              value > 1
                                  ? `${value} campaigns`
                                  : `${value} campaign`
                          }`
                        : '-',
            }
        ],
        expandedColDefs:[
            {
                title: "CAMPAIGN NAME",
                dataIndex: "campaignName",
                key: "campaignName",
                width: 350,
                onHeaderCell: () => ({
                    style: { paddingLeft: '16px' }
                }),
                onCell: () => ({
                    style: { verticalAlign: 'middle', paddingLeft: '16px' }
                }),
                render: (value: string, record: any) => (
                    <EditableCampaignNameCell
                        value={value}
                        record={record}
                        handleCampaignNameChange={handleCampaignNameChange}
                    />
                ),
                sorter: (a: any, b: any) => {
                    const aVal = a.campaignName || '';
                    const bVal = b.campaignName || '';
                    return aVal.localeCompare(bVal);
                },
            },
            {
                title: "BUDGET",
                dataIndex: "budget",
                key: "budget",
                width: 200,
                align: "left",
                onCell: () => ({
                    style: { verticalAlign: 'middle' }
                }),
                render: (value: string) => {
                    const formattedValue = value ? internationalizeNumericValue(value) : '';
                    return (
                        <Input
                            value={formattedValue}
                            prefix={clientCurrencySymbol}
                            readOnly
                            style={{ opacity: 0.7 }}
                        />
                    );
                },
                sorter: (a: any, b: any) => {
                    const aVal = a.budget || 0;
                    const bVal = b.budget || 0;
                    return aVal - bVal;
                },
            },
            {
                title: "CAMPAIGN TYPE",
                dataIndex: "campaignType",
                key: "campaignType",
                width: 180,
                align: "left",
                onCell: () => ({
                    style: { verticalAlign: 'middle' }
                })
            },
            {
                title: "KEYWORD TYPE",
                dataIndex: "keywordType",
                key: "keywordType",
                width: 180,
                align: "left",
                onCell: () => ({
                    style: { verticalAlign: 'middle' }
                }),
                render: (value: string, record: any) => {
                    // Use cohortLabel if available, otherwise format keywordType
                    let displayLabel = '';
                    if (record?.cohortLabel) {
                        // cohortLabel is like "Generic" or "Generic + Branded" or "Categories"
                        // Abbreviate each part: Generic -> G, Branded -> B, Competition -> C, Categories -> Category
                        const abbreviations: Record<string, string> = {
                            'gen': 'G',
                            'brand': 'B',
                            'comp': 'C',
                            'category': 'Category'
                        };
                        displayLabel = record.cohortLabel
                            .split(' + ')
                            .map((part: string) => abbreviations[part.trim().toLowerCase()] || titleCase(part.trim()))
                            .join(' + ');
                    } else if (value) {
                        // Fallback: split by comma and take first char
                        displayLabel = value
                            .split(",")
                            .map((item: string) => item.trim().charAt(0).toUpperCase())
                            .join("/");
                    }
                    
                    return (
                        <Tag
                            style={{
                                backgroundColor: "#E5F8FF",
                                color: "#0078AA",
                                border: "1px solid #0078AA",
                                borderRadius: "8px",
                                padding: "4px 8px",
                                fontSize: "14px",
                                fontWeight: "700",
                                margin: 0,
                            }}
                        >
                            {displayLabel || '-'}
                        </Tag>
                    );
                },
            },
            {
                title: "CITY",
                dataIndex: "city",
                key: "city",
                width: 180,
                align: "left",
                onCell: () => ({
                    style: { verticalAlign: 'middle' }
                }),
                render: CityCellRenderer
            },
            {
                title: "TIME SLOT",
                dataIndex: "timeSlot",
                key: "timeSlot",
                width: 180,
                align: "left",
                onCell: () => ({
                    style: { verticalAlign: 'middle' }
                })
            }
        ],
        isGroupedTable: false,
        isSearchReq: true,
        isDrilldownTable: false,
        isCompetitionButtonReq: false,
        isLayoutButtonReq: false,
        isDownloadButtonReq: true,
        isExpandable: true,
        isCheckboxSelectionRequired: false,
        showExpandColumn: true,
        bordered: false,
        style: {
            height: "100%",
        },
        expandedRowKeys,
        setExpandedRowKeys,
        expandedRowRender,
        rowExpandable,
    };
};

export const getCampaignCreationKeywordsTableConfig = (params: any) => {
    const {
        handleFieldChange,
        handleBatchKeywordFieldUpdates,
        clientCurrencySymbol,
        selectedKeywords,
        selectedKwTypes,
        minExactMatchBid,
    } = params;
    const isSelected = (record: any) =>
        selectedKeywords?.filter((kwd: any) => kwd.key === record.key)?.length >
        0;

    return {
        colDefs: [
            {
                title: "KEYWORDS",
                dataIndex: "keyword",
                key: "keyword",
                width: 214,
                align: "left",
                fixed: "left",
                render: (text: string) => (
                    <span style={{ fontWeight: 600, paddingLeft: "5px" }}>
                        {startCase(text)}
                    </span>
                ),
                sorter: (a: IBCCKeyword, b: IBCCKeyword) => {
                    const aVal = a.keyword || "";
                    const bVal = b.keyword || "";
                    return aVal.localeCompare(bVal);
                },
            },
            {
                title: "KEYWORD TYPE",
                dataIndex: "keyword_type",
                key: "keyword_type",
                width: 110,
                align: "center",
                render: (text: string) => (
                    <span style={{ fontWeight: 600, paddingLeft: "5px" }}>
                        {titleCase(text)}
                    </span>
                ),
                sorter: (a: IBCCKeyword, b: IBCCKeyword) => {
                    const aVal = a.keyword_type || "";
                    const bVal = b.keyword_type || "";
                    return aVal.localeCompare(bVal);
                },
            },
            {
                title: "SEARCH VOLUME",
                dataIndex: "search_vol",
                key: "search_vol",
                width: 104,
                align: "center",
                render: (value: number) => (
                    <span style={{ fontWeight: 600, paddingLeft: "5px" }}>
                        {value ? internationalizeNumericValue(value) : "-"}
                    </span>
                ),
                sorter: (a: IBCCKeyword, b: IBCCKeyword) => {
                    const aVal =
                        a.search_vol ||
                        0;
                    const bVal =
                        b.search_vol ||
                        0;
                    return aVal - bVal;
                },
            },
            {
                title: "ORGANIC SOV",
                dataIndex: "organic_sov",
                key: "organic_sov",
                width: 100,
                align: "center",
                render: (value: number, record: IBCCKeyword) =>
                    ChangeCellRenderer(
                        CommonWidgetMetrics.ORGANIC_SOV,
                        value,
                        record[`${CommonWidgetMetrics.ORGANIC_SOV}_change`],
                        "bottom"
                    ),
                sorter: (a: IBCCKeyword, b: IBCCKeyword) => {
                    const aVal =
                        a[`${CommonWidgetMetrics.ORGANIC_SOV}`] ||
                        0;
                    const bVal =
                        b[`${CommonWidgetMetrics.ORGANIC_SOV}`] ||
                        0;
                    return aVal - bVal;
                },
            },
            {
                title: "PAID SOV",
                dataIndex: "sponsored_sov",
                key: "sponsored_sov",
                width: 100,
                align: "center",
                render: (value: number, record: any) =>
                    ChangeCellRenderer(
                        CommonWidgetMetrics.PAID_SOV,
                        value,
                        record[`${CommonWidgetMetrics.SPONSORED_SOV}_change`],
                        "bottom"
                    ),
                sorter: (a: IBCCKeyword, b: IBCCKeyword) => {
                    const aVal =
                        a[`${CommonWidgetMetrics.SPONSORED_SOV}`] ||
                        0;
                    const bVal =
                        b[`${CommonWidgetMetrics.SPONSORED_SOV}`] ||
                        0;
                    return aVal - bVal;
                },
            },
            {
                title: "ORGANIC RANK",
                dataIndex: "organic_rank",
                key: "organic_rank",
                width: 100,
                align: "center",
                render: (value: number, record: any) =>
                    ChangeCellRenderer(
                        CommonWidgetMetrics.ORGANIC_RANK,
                        value,
                        -record[`${CommonWidgetMetrics.ORGANIC_RANK}_change`],
                        "bottom",
                        undefined,
                        undefined,
                        "", // change unit suffix,
                        0 // decimal places
                    ),
                sorter: (a: IBCCKeyword, b: IBCCKeyword) => {
                    const aVal =
                        a[`${CommonWidgetMetrics.ORGANIC_RANK}`] ||
                        0;
                    const bVal =
                        b[`${CommonWidgetMetrics.ORGANIC_RANK}`] ||
                        0;
                    return bVal - aVal;
                },
            },
            {
                title: "PAID RANK",
                dataIndex: "paid_rank",
                key: "paid_rank",
                width: 100,
                align: "center",
                render: (value: number, record: any) =>
                    ChangeCellRenderer(
                        CommonWidgetMetrics.PAID_RANK,
                        value,
                        -record[`${CommonWidgetMetrics.PAID_RANK}_change`],
                        "bottom",
                        undefined,
                        undefined,
                        "", // change unit suffix
                        0 // decimal places
                    ),
                sorter: (a: IBCCKeyword, b: IBCCKeyword) => {
                    const aVal =
                        a[`${CommonWidgetMetrics.PAID_RANK}`] ||
                        0;
                    const bVal =
                        b[`${CommonWidgetMetrics.PAID_RANK}`] ||
                        0;
                    return  bVal - aVal;
                },
            },
            {
                title: "EXACT MATCH BID",
                dataIndex: "exact_match_bid",
                key: "exact_match_bid",
                width: 108,
                align: "center",
                render: (value: number, record: any) => (
                    <ExactMatchBidInput
                        value={value}
                        record={record}
                        fieldName={KWD_BID_TYPES.EXACT_MATCH_BID}
                        handleFieldChange={handleFieldChange}
                        currencySymbol={clientCurrencySymbol}
                        isSelected={isSelected(record)}
                    />
                ),
            },
            {
                title: "BID BOOSTER",
                dataIndex: "bid_booster",
                key: "bid_booster",
                width: 108,
                align: "center",
                render: (value: string, record: any) => (
                    <BidCellRenderer
                        value={value}
                        record={record}
                        fieldName="bid_booster"
                        handleFieldChange={handleFieldChange}
                        isSelected={isSelected(record)}
                    />
                ),
            },
            {
                title: "SMART MATCH BID",
                dataIndex: "smart_match_bid",
                key: "smart_match_bid",
                width: 108,
                align: "center",
                render: (value: string, record: any) => (
                    <SmartMatchBidCell
                        value={value}
                        record={record}
                        handleFieldChange={handleFieldChange}
                        currencySymbol={clientCurrencySymbol}
                        isSelected={isSelected(record)}
                    />
                ),
            },
        ],
        isGroupedTable: false,
        isSearchReq: true,
        isDrilldownTable: false,
        isCompetitionButtonReq: false,
        isLayoutButtonReq: false,
        isDownloadButtonReq: true,
        isExpandable: false,
        isCheckboxSelectionRequired: true,
        style: {
            height: "40vh",
        },
        additionalFilters: [
            {
                section: "",
                key: CommonWidgetMetrics.CATEGORY,
                type: RevstackFilterTypes.DROPDOWN_RADIO,
                props: {
                    options: [
                        { value: BCC_BID_SUGGESTION_TYPES.EXACT_SUGGESTED_MIN, label: "Suggested Minimum" },
                        { value: BCC_BID_SUGGESTION_TYPES.EXACT_SUGGESTED_MAX, label: "Suggested Maximum" },
                        { value: BCC_BID_SUGGESTION_TYPES.MIN_BID_LIMIT, label: "Mandatory Minimum" },
                    ],
                    defaultValue: "",
                    dropdownButtonText: "Auto Suggest Bids",
                    submitBtnText: "Suggest",
                    placement: "bottom",
                    disabled : selectedKeywords?.length === 0 || 
                    selectedKeywords?.every((kwd: any) => kwd?.keyword_type === BCC_KEYWORD_TYPES.NEGATIVE),
                    onSubmit: (selectedOption: string) => {
                        if (!selectedOption || !selectedKeywords?.length) return;
                    
                        // Collect all updates to batch them together
                        const updates: Array<{ clusterId: string; collectionId: string; field: string; value: any; record: any }> = [];
                        
                        selectedKeywords.forEach((kwd: any) => {
                            // Skip negative keywords
                            if (kwd?.keyword_type === BCC_KEYWORD_TYPES.NEGATIVE) return;
                    
                            // For custom keywords, use minExactMatchBid since they don't have suggested values
                            if (kwd?.keyword_type === BCC_KEYWORD_TYPES.CUSTOM || kwd?.isCustom) {
                                const customBidValue = Number(minExactMatchBid) || 0;
                                if (customBidValue > 0) {
                                    updates.push({
                                        clusterId: kwd.clusterId,
                                        collectionId: kwd.id,
                                        field: KWD_BID_TYPES.EXACT_MATCH_BID,
                                        value: customBidValue,
                                        record: kwd
                                    });
                                }
                                return;
                            }
                    
                            const rawBid = kwd[selectedOption];
                            const bidValue = Number(rawBid);
                            const isValidBid = !isNaN(bidValue) && rawBid != null;
                    
                            if (!isValidBid) return;
                    
                            updates.push({
                                clusterId: kwd.clusterId,
                                collectionId: kwd.id,
                                field: KWD_BID_TYPES.EXACT_MATCH_BID,
                                value: bidValue,
                                record: kwd
                            });
                        });
                        
                        // Apply all updates in a single batched call
                        if (updates.length > 0 && handleBatchKeywordFieldUpdates) {
                            handleBatchKeywordFieldUpdates(updates);
                        } else if (updates.length > 0) {
                            // Fallback to individual updates if batched function not available
                            updates.forEach(update => {
                                handleFieldChange(
                                    update.clusterId,
                                    update.collectionId,
                                    update.field,
                                    update.value,
                                    update.record
                                );
                            });
                        }
                    }
                },
            },
        ],
    };
};

export const getCampaignCreationCategoriesTableConfig = (params: any) => {

    const {
        handleFieldChange,
        handleBatchCategoryFieldUpdates,
        clientCurrencySymbol,
        selectedTargetCategories,
    } = params;


    const isSelected = (record: any) =>
        selectedTargetCategories?.filter((cat: any) => cat.key === record.key)?.length >0;
    return {
        colDefs: [
            {
                title: "CATEGORIES",
                dataIndex: "category_name",
                key: "category",
                width: 120,
                align: "left",
                fixed: "left",
                render: (text: string) => (
                    <span style={{ fontWeight: 600, paddingLeft: "5px" }}>
                        {startCase(text)}
                    </span>
                ),
                sorter: (a: ICategoryTarget, b: ICategoryTarget) => {
                    const aVal = a.category_name || "";
                    const bVal = b.category_name || "";
                    return aVal.localeCompare(bVal);
                },
            },
            {
                title: "CATEGORY VISITS",
                dataIndex: "search_vol",
                key: "search_vol",
                width: 104,
                align: "center",
                render: (value: string) => (
                    <span style={{ fontWeight: 600, paddingLeft: "5px" }}>
                        {value ? internationalizeNumericValue(value) : "-"}
                    </span>
                ),
                sorter: (a: ICategoryTarget, b: ICategoryTarget) => {
                    const aVal =
                        a.search_vol ||
                        0;
                    const bVal =
                        b.search_vol ||
                        0;
                    return aVal - bVal;
                },
            },
            {
                title: "CPM BID",
                dataIndex: "cpm_bid",
                key: "cpm_bid",
                width: 180,
                align: "center",
                render: (value: number, record: any) => (
                    <ExactMatchBidInput
                        value={value}
                        record={record}
                        fieldName="cpm_bid"
                        handleFieldChange={handleFieldChange}
                        currencySymbol={clientCurrencySymbol}
                        isSelected={isSelected(record)}
                    />
                ),
            },
        ],
        isGroupedTable: false,
        isSearchReq: true,
        isDrilldownTable: false,
        isCompetitionButtonReq: false,
        isLayoutButtonReq: false,
        isDownloadButtonReq: true,
        isExpandable: false,
        isCheckboxSelectionRequired: true,
        style: {
            height: "40vh",
        },
        additionalFilters: [
            {
                section: "",
                key: CommonWidgetMetrics.CATEGORY,
                type: RevstackFilterTypes.DROPDOWN_RADIO,
                props: {
                    options: [
                        { value: BCC_BID_SUGGESTION_TYPES.EXACT_SUGGESTED_MIN, label: "Suggested Minimum" },
                        { value: BCC_BID_SUGGESTION_TYPES.EXACT_SUGGESTED_MAX, label: "Suggested Maximum" },
                        { value: BCC_BID_SUGGESTION_TYPES.MIN_BID_LIMIT, label: "Mandatory Minimum" },
                    ],
                    defaultValue: "",
                    dropdownButtonText: "Auto Suggest Bids",
                    submitBtnText: "Suggest",
                    placement: "bottom",
                    disabled : selectedTargetCategories?.length === 0 || 
                    selectedTargetCategories?.every((kwd: any) => kwd?.keyword_type === BCC_KEYWORD_TYPES.NEGATIVE),
                    onSubmit: (selectedOption: string) => {
                        if (!selectedOption || !selectedTargetCategories?.length) return;
                    
                        // Collect all updates to batch them together
                        const updates: Array<{ clusterId: string; collectionId: string; field: string; value: any; record: any }> = [];
                        
                        selectedTargetCategories.forEach((kwd: any) => {
                            const rawBid = kwd[selectedOption];
                            const bidValue = Number(rawBid);
                            const isValidBid = !isNaN(bidValue) && rawBid != null;
                    
                            if (!isValidBid) return;
                    
                            updates.push({
                                clusterId: kwd.clusterId,
                                collectionId: kwd.id,
                                field: KWD_BID_TYPES.CPM_BID,
                                value: bidValue,
                                record: kwd
                            });
                        });
                        
                        // Apply all updates in a single batched call
                        if (updates.length > 0 && handleBatchCategoryFieldUpdates) {
                            handleBatchCategoryFieldUpdates(updates);
                        } else if (updates.length > 0) {
                            // Fallback to individual updates if batched function not available
                            updates.forEach(update => {
                                handleFieldChange(
                                    update.clusterId,
                                    update.collectionId,
                                    update.field,
                                    update.value,
                                    update.record
                                );
                            });
                        }
                    }
                },
            },
        ],
    };
};

export const getCampaignCreationAssetsTableConfig = (params: any) => {

    const {
        handleFieldChange,
        clientCurrencySymbol,
        selectedTargetCategories,
    } = params;


    const isSelected = (record: any) =>
        selectedTargetCategories?.filter((cat: any) => cat.key === record.key)?.length >0;
    return {
        colDefs: [
            {
                title: "ASSET",
                dataIndex: "name",
                key: "name",
                width: 280,
                render: (value: string, record: any) => (
                    <div className="asset-title-container">
                        <div className="asset-title-text">
                            {value}
                        </div>
                        <div className="asset-description-text">{record.desc}</div>
                    </div>
                ),
            },
            {
                title: "CPM BID",
                dataIndex: "cpm_bid",
                key: "cpm_bid",
                width: 300,
                align: "center",
                render: (value: number, record: any) => (
                    <ExactMatchBidInput
                        value={value}
                        record={record}
                        fieldName="cpm_bid"
                        handleFieldChange={handleFieldChange}
                        currencySymbol={clientCurrencySymbol}
                        isSelected={isSelected(record)}
                        width={200}
                        bidRangePosition="side"
                    />
                ),
            },
        ],
        isGroupedTable: false,
        isSearchReq: false,
        isDrilldownTable: false,
        isCompetitionButtonReq: false,
        isLayoutButtonReq: false,
        isDownloadButtonReq: false,
        isExpandable: false,
        isCheckboxSelectionRequired: true,
        style: {
            height: 300,
        },
        additionalFilters: [
            {
                section: "",
                key: CommonWidgetMetrics.CATEGORY,
                type: RevstackFilterTypes.DROPDOWN_RADIO,
                props: {
                    options: [
                        { value: BCC_BID_SUGGESTION_TYPES.EXACT_SUGGESTED_MIN, label: "Suggested Minimum" },
                        { value: BCC_BID_SUGGESTION_TYPES.EXACT_SUGGESTED_MAX, label: "Suggested Maximum" },
                        { value: BCC_BID_SUGGESTION_TYPES.MIN_BID_LIMIT, label: "Mandatory Minimum" },
                    ],
                    defaultValue: "",
                    dropdownButtonText: "Auto Suggest Bids",
                    submitBtnText: "Suggest",
                    placement: "bottom",
                    // disabled : selectedKeywords?.length === 0 || 
                    // selectedKeywords?.every((kwd: any) => kwd?.keyword_type === BCC_KEYWORD_TYPES.NEGATIVE),
                    onSubmit: (selectedOption: string) => {
                        // console.log("selectedOption", selectedOption);
                    }
                },
            },
        ],
    };
};